import { useTranslation } from "react-i18next";

interface LandingFooterProps {
  onNavigate: (path: string) => void;
}

/**
 * Ícones de marca (Instagram/LinkedIn) como SVG inline: esta versão do
 * lucide-react não inclui glifos de marca, então os desenhamos aqui.
 */
function InstagramIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

function LinkedinIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

/** Footer da landing — continua o bloco ink do CTA final. */
export function LandingFooter({ onNavigate }: LandingFooterProps) {
  const { t } = useTranslation("landing");
  return (
    <footer className="bg-ink border-t border-white/10 py-10">
      <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-5 text-center md:text-left">
        <div className="flex flex-col items-center md:items-start gap-1.5">
          <img src="/logo-taskletto-light.png" alt="Taskletto" className="h-6 w-auto opacity-80" />
          <p className="text-[11px] text-primary-200/50">{t("footer.tagline")}</p>
        </div>
        <p className="text-xs text-white/40">
          © {new Date().getFullYear()} Taskletto. {t("footer.rights")}
        </p>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex items-center gap-4 text-xs text-primary-200/60">
            <button
              onClick={() => onNavigate("/sobre")}
              className="hover:text-primary-200 transition-colors cursor-pointer border-none bg-transparent font-medium"
            >
              {t("footer.about")}
            </button>
            <button
              onClick={() => onNavigate("/terms")}
              className="hover:text-primary-200 transition-colors cursor-pointer border-none bg-transparent font-medium"
            >
              {t("footer.terms")}
            </button>
            <button
              onClick={() => onNavigate("/privacy")}
              className="hover:text-primary-200 transition-colors cursor-pointer border-none bg-transparent font-medium"
            >
              {t("footer.privacy")}
            </button>
            <a
              href="mailto:suporte@taskletto.app"
              className="hover:text-primary-200 transition-colors font-medium"
            >
              {t("footer.contact")}
            </a>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://www.instagram.com/taskletto/"
              target="_blank"
              rel="noopener noreferrer"
              title={t("footer.instagram")}
              aria-label={t("footer.instagram")}
              className="p-2 rounded-full text-primary-200/60 hover:text-primary-200 hover:bg-white/5 transition-colors"
            >
              <InstagramIcon />
            </a>
            <a
              href="https://www.linkedin.com/company/taskletto/"
              target="_blank"
              rel="noopener noreferrer"
              title={t("footer.linkedin")}
              aria-label={t("footer.linkedin")}
              className="p-2 rounded-full text-primary-200/60 hover:text-primary-200 hover:bg-white/5 transition-colors"
            >
              <LinkedinIcon />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
